/**
 * Theme Events — Typed custom events for cross-component communication.
 *
 * All events bubble so they can be listened to on `document`.
 * Components dispatch events on themselves; consumers listen on
 * `document` (global) or a specific ancestor (scoped).
 *
 * Usage:
 *   import { ThemeEvents, CartAddEvent } from '@theme/events';
 *   document.addEventListener(ThemeEvents.cartAdd, handler);
 *   this.dispatchEvent(new CartAddEvent(data, sectionId));
 */

/* ── Event name constants ── */

export const ThemeEvents = {
  variantSelected: 'theme:variant:selected',
  variantUpdate: 'theme:variant:update',
  cartAdd: 'theme:cart:add',
  cartUpdate: 'theme:cart:update',
  cartError: 'theme:cart:error',
  quantityUpdate: 'theme:quantity:update',
  mediaPlaying: 'theme:media:playing',
  filterUpdate: 'theme:filter:update',
  megaMenuHover: 'theme:mega-menu:hover',
};

/* ── Event classes ── */

/**
 * Fired when a variant is selected on a product form.
 */
export class VariantSelectedEvent extends Event {
  /** @param {object} variant */
  constructor(variant) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = { variant };
  }
}

/**
 * Fired when variant state changes (price, availability, images).
 */
export class VariantUpdateEvent extends Event {
  /** @param {object} data - { variant, html, productId, sectionId } */
  constructor(data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = { data };
  }
}

/**
 * Fired after a successful add-to-cart.
 */
export class CartAddEvent extends Event {
  /**
   * @param {object} data - The cart response data.
   * @param {string} sectionId - The section that triggered the add.
   */
  constructor(data, sectionId) {
    super(ThemeEvents.cartAdd, { bubbles: true });
    this.detail = { data, sectionId };
  }
}

/**
 * Fired after any cart mutation (add, remove, update quantity).
 */
export class CartUpdateEvent extends Event {
  /**
   * @param {object} data - The full cart response.
   * @param {string} sectionId - The section that triggered the update.
   * @param {object} [extra] - { itemCount, sections, source }
   */
  constructor(data, sectionId, extra) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      data,
      sectionId,
      itemCount: extra?.itemCount,
      sections: extra?.sections,
      source: extra?.source,
    };
  }
}

/**
 * Fired on cart API errors.
 */
export class CartErrorEvent extends Event {
  /**
   * @param {string} message
   * @param {string} [description]
   * @param {object} [errors]
   */
  constructor(message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = { message, description, errors };
  }
}

/**
 * Fired when a quantity selector changes.
 */
export class QuantityUpdateEvent extends Event {
  /**
   * @param {number} quantity
   * @param {number} [cartLine] - 1-indexed cart line number
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantityUpdate, { bubbles: true });
    this.detail = { quantity, cartLine };
  }
}

/**
 * Fired when collection filters change.
 */
export class FilterUpdateEvent extends Event {
  /** @param {string} queryParams */
  constructor(queryParams) {
    super(ThemeEvents.filterUpdate, { bubbles: true });
    this.detail = { queryParams };
  }
}
